/**
 * Analytics Provider for Colony Marketing Websites.
 *
 * Phase 28 H2 rewire (2026-05-19): drops the legacy `window.gtag` emit
 * path and routes every event through the canonical
 * `createMarketingAnalytics` factory, which adds:
 *   - `marketing.<appKey>.<event>` namespace prefix (CONVERSION_EVENTS-gated)
 *   - First-touch + last-touch UTM attribution (utmCapture)
 *   - Session-id tagging (30-min TTL)
 *   - Pre-consent buffering + flush-on-grant
 *
 * Privacy-first / GDPR-compliant: methods no-op until cookie consent has
 * been given. The factory's `getConsent` callback is wired to the
 * `useCookieConsent` hook so consent state flows end-to-end.
 *
 * Public API surface (preserved for source-compat with port-web / purchase-web
 * / payday-web / palm-web / patrol-web / planner-web / prep-web ContactPages):
 *   - trackPageView(path, title)        → `page_view`
 *   - trackEvent(category, action, lbl) → maps `action` → CONVERSION_EVENT if registered (else silent no-op)
 *   - track(eventName, properties)      → CONVERSION_EVENTS-gated direct emit
 *   - trackConversion(name, properties) → CONVERSION_EVENTS-gated direct emit
 *   - trackScrollDepth(percentage)      → `scroll_depth`
 *   - isEnabled                         → consent && transport-wired
 *
 * Required props for live emit: `appKey`, `transport`, `storageAdapter`.
 * When ANY of these is missing, the provider degrades to no-op mode (tests
 * + legacy callers that haven't wired Phase 13 yet still mount cleanly).
 *
 * A `transport` brings a REQUIRED `onTransportError` (REQUEST-VALIDATION-CONVERGE-001 AN2): the
 * provider composes GA4 onto the app's transport and hands the reporter to the GA4 leg and to the
 * composer, so a failed send is reported, never swallowed. Given a transport without one, the
 * provider refuses by name at render.
 *
 * @module @puffin-core/marketing-shell/AnalyticsProvider
 */

import { createContext, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useCookieConsent } from './CookieConsent.jsx';
import createMarketingAnalytics from '../factories/createMarketingAnalytics.js';
import loadGoogleConsentMode, { updateGoogleConsent } from '../analytics/loadGoogleConsentMode.js';
import createGa4Transport from '../analytics/createGa4Transport.js';
import { composeTransports } from '../analytics/createMarketingTransport.js';
import { resolveGa4MeasurementId } from '../analytics/ga4Measurement.js';
import { isConversionEvent } from '../analytics/conversionEvents.js';

const AnalyticsContext = createContext(null);

/**
 * The refusal for a transport handed over without a reporter. A render throw, because React 19 runs
 * no propTypes: a propType would never fire, and the failures it guards would vanish again.
 *
 * @param {unknown} transport
 * @param {unknown} onTransportError
 * @throws {TypeError} when a transport is given and `onTransportError` is not a function
 */
function refuseUnreportedTransport(transport, onTransportError) {
  if (transport && typeof onTransportError !== 'function') {
    throw new TypeError(
      'AnalyticsProvider: onTransportError (error) => void is required when a transport is given — a transport failure is reported, never swallowed',
    );
  }
}

/**
 * Custom hook to access analytics tracking methods.
 *
 * @returns {Object} Analytics methods + state
 * @throws {Error} when used outside AnalyticsProvider
 */
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

/**
 * Analytics provider component.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.appKey]                Marketing appKey. Required for live emit.
 * @param {{track: (envelope) => void}} [props.transport]  Transport adapter.
 * @param {{get: (key) => string|null, set: (key, value) => void}} [props.storageAdapter]
 *   Browser-storage adapter (localStorage shape). Required for UTM + session tagging.
 * @param {(error: Error) => void} [props.onTransportError]  Required whenever `transport` is given:
 *   receives every failure of the GA4 leg and of the composer the provider builds around it.
 * @returns {React.JSX.Element}
 */
/**
 * useMarketingAnalyticsInstance — owns the consent read, the always-current
 * consent ref the analytics instance closes over, the memoized instance itself,
 * and the flush-on-consent effect.
 *
 * The consent ref is what lets the instance be memoized on the wiring props
 * ALONE (it reads consent lazily at track time), so a consent flip never tears
 * down and rebuilds the pipeline — it just flushes the pre-consent buffer.
 *
 * @returns {{hasConsent: boolean, marketingAnalytics: object|null}}
 */
function useMarketingAnalyticsInstance({ appKey, transport, storageAdapter, onTransportError }) {
  const { hasConsent } = useCookieConsent();
  const consentRef = useRef(hasConsent);
  consentRef.current = hasConsent;

  // AISEO-GA4-001 — GA4 is attached HERE rather than by each app passing it, so the nine
  // `MarketingWebsiteApp.jsx` files need no edit and cannot drift apart on analytics wiring (which
  // is exactly how five of them ended up with subtly different marketing setups before). The ID
  // resolves from the hostname, so the three vanity brands served by purchase-web's single bundle
  // each report into their OWN property. An unmapped host — localhost, preview, tunnel — resolves
  // to undefined and gets no tag, keeping dev traffic out of production properties.
  const ga4MeasurementId = resolveGa4MeasurementId();

  // Load gtag.js with Consent Mode v2 defaults installed FIRST. `loadGoogleConsentMode` is
  // idempotent, so StrictMode's double-effect and client-side route changes cannot double-load it.
  useEffect(() => {
    if (!ga4MeasurementId) return;
    loadGoogleConsentMode({ measurementId: ga4MeasurementId });
  }, [ga4MeasurementId]);

  // Mirror the cookie-banner decision into the live tag. Only meaningful in the prior-consent
  // regions (elsewhere the default is already granted), but harmless everywhere.
  useEffect(() => {
    if (!ga4MeasurementId) return;
    updateGoogleConsent({ granted: hasConsent });
  }, [ga4MeasurementId, hasConsent]);

  // Fan out to the app's own first-party ingest AND GA4. Composed rather than substituted: the
  // first-party copy is the one we own and the one that survives ad-blockers, so it must never be
  // displaced by the third-party tag.
  // Both the GA4 leg and the composer report through the app's `onTransportError`.
  const composedTransport = useMemo(() => {
    if (!transport) return transport;
    if (!ga4MeasurementId) return transport;
    return composeTransports(
      [
        transport,
        createGa4Transport({ measurementId: ga4MeasurementId, onError: onTransportError }),
      ],
      onTransportError,
    );
  }, [transport, ga4MeasurementId, onTransportError]);

  // Build the canonical marketing-analytics instance when all 3 wiring
  // props are supplied. When ANY is missing the provider runs in no-op
  // mode (tests / legacy callers stay green).
  const marketingAnalytics = useMemo(() => {
    if (!appKey || !composedTransport || !storageAdapter) return null;
    return createMarketingAnalytics({
      appKey,
      transport: composedTransport,
      storageAdapter,
      getConsent: () => consentRef.current,
      bufferPreConsent: true,
    });
  }, [appKey, composedTransport, storageAdapter]);

  // Flush any pre-consent buffered events the moment consent flips true.
  useEffect(() => {
    if (hasConsent && marketingAnalytics) {
      marketingAnalytics.flushQueuedOnConsent();
    }
  }, [hasConsent, marketingAnalytics]);

  return { hasConsent, marketingAnalytics };
}

export function AnalyticsProvider({
  children,
  appKey,
  transport,
  storageAdapter,
  onTransportError,
}) {
  refuseUnreportedTransport(transport, onTransportError);
  const { hasConsent, marketingAnalytics } = useMarketingAnalyticsInstance({
    appKey,
    transport,
    storageAdapter,
    onTransportError,
  });

  // safeEmit centralizes the CONVERSION_EVENTS gate + the transport-availability
  // check. Non-canonical event names silently no-op (Gen-0: the registry is
  // the SSOT; unknown events do not propagate to ingest).
  const safeEmit = useCallback(
    (eventName, properties) => {
      if (!marketingAnalytics) return;
      if (!isConversionEvent(eventName)) return;
      marketingAnalytics.track(eventName, properties);
    },
    [marketingAnalytics],
  );

  const trackPageView = useCallback(
    (path, title) => {
      safeEmit('page_view', { path, title });
    },
    [safeEmit],
  );

  // Legacy GA-style trackEvent(category, action, label). The `action`
  // parameter is the canonical CONVERSION_EVENT name; if it isn't
  // registered, the call silently drops. Existing app callers that
  // emit non-canonical actions (e.g. `trackEvent('contact', 'success', id)`)
  // become no-ops — those signals are now carried via the marketing-contact
  // factory's `onOutcome` hook (Phase 27 G6 wiring).
  const trackEvent = useCallback(
    (category, action, label) => {
      safeEmit(action, { category, label });
    },
    [safeEmit],
  );

  const trackScrollDepth = useCallback(
    (percentage) => {
      safeEmit('scroll_depth', { depth: percentage });
    },
    [safeEmit],
  );

  const value = useMemo(
    () => ({
      trackPageView,
      trackEvent,
      // `track` and `trackConversion` ARE `safeEmit`: `(eventName, properties)`, a CONVERSION_EVENT name expected.
      // The second name is for callers that distinguish conversion events from generic ones.
      track: safeEmit,
      trackConversion: safeEmit,
      trackScrollDepth,
      isEnabled: Boolean(hasConsent && marketingAnalytics),
    }),
    [trackPageView, trackEvent, safeEmit, trackScrollDepth, hasConsent, marketingAnalytics],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

AnalyticsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  appKey: PropTypes.string,
  transport: PropTypes.shape({ track: PropTypes.func.isRequired }),
  storageAdapter: PropTypes.shape({
    get: PropTypes.func.isRequired,
    set: PropTypes.func.isRequired,
  }),
  onTransportError: PropTypes.func,
};

export default AnalyticsProvider;
